import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { classNames } from "primereact/utils";

import { translationKeys } from "../../../../models/app-translation-keys";
import { sharedDialog, useSharedDialogState } from "../../../Shared/Components/Dialogs/SharedDialog";
import { TooltipWrapper } from "../../../Shared/Components/TooltipWrapper/TooltipWrapper";
import { ITermsOfUseDialogProps } from "../TermsOfUseDialog";
import { ITermsOfUseDialogState } from "../termsOfUseDialogTypes";

import "./TermsOfUseDialogFooter.scss";

interface ITermsOfUseDialogFooterProps {
  dialogId?: string;
  base64PdfDocument: ITermsOfUseDialogProps["base64PdfDocument"];
  onAccept: ITermsOfUseDialogProps["onAccept"];
  onDecline: ITermsOfUseDialogProps["onDecline"];
}

const termsOfUseAndDataPrivacyPolicyKeys = translationKeys.termsOfUseAndDataPrivacyPolicy;

export const TermsOfUseDialogFooter: FunctionComponent<ITermsOfUseDialogFooterProps> = ({
  dialogId,
  base64PdfDocument,
  onAccept,
  onDecline,
}: ITermsOfUseDialogFooterProps) => {
  const { t } = useTranslation();
  const { sharedDialogState, setSharedDialogState } = useSharedDialogState<ITermsOfUseDialogState>();

  const onAcceptTermsOfUseAndPrivacyPolicy = async (): Promise<void> => {
    if (!sharedDialogState.isScrolledToBottom) {
      return;
    }

    await onAccept();

    if (dialogId) {
      sharedDialog.closeSharedDialog(dialogId);
    }
  };

  const onDeclineTermsOfUseAndPrivacyPolicy = (): void => {
    onDecline();

    if (dialogId) {
      sharedDialog.closeSharedDialog(dialogId);
    }
  };

  const onCheckedChanged = (checked: boolean): void => {
    setSharedDialogState(previousState => {
      if (previousState.isChecked === checked) {
        return previousState;
      }

      return {
        ...previousState,
        isChecked: checked,
      };
    });
  };

  return (
    <div className="dialog-footer">
      <TooltipWrapper
        targetRef=".terms-of-use-checkbox"
        position="top"
        disabled={sharedDialogState.isScrolledToBottom}
        content={t(termsOfUseAndDataPrivacyPolicyKeys.tooltips.unread)}
      />

      <div className="dialog-footer-left">
        <div
          className={classNames("terms-of-use-checkbox", {
            disabled: !sharedDialogState.isScrolledToBottom,
          })}
        >
          <Checkbox
            inputId="checkboxId"
            className="accept-terms-of-use"
            disabled={!sharedDialogState.isScrolledToBottom || !base64PdfDocument}
            checked={sharedDialogState.isChecked}
            onChange={event => onCheckedChanged(Boolean(event.checked))}
          />

          <label htmlFor="checkboxId" className="dialog-footer-label accept-terms-of-use">
            {t(termsOfUseAndDataPrivacyPolicyKeys.checkboxText)}
          </label>
        </div>
      </div>

      <div className="dialog-footer-buttons">
        <div className="decline-button">
          <TooltipWrapper
            targetRef=".decline-button"
            position="top"
            content={t(termsOfUseAndDataPrivacyPolicyKeys.tooltips.decline)}
          />

          <Button
            label={t(termsOfUseAndDataPrivacyPolicyKeys.actions.decline)}
            severity="danger"
            onClick={onDeclineTermsOfUseAndPrivacyPolicy}
          />
        </div>

        <div className="accept-terms-of-use-button">
          <TooltipWrapper
            targetRef=".accept-terms-of-use-button"
            disabled={sharedDialogState.isChecked}
            position="top"
            content={t(termsOfUseAndDataPrivacyPolicyKeys.tooltips.unchecked)}
          />

          <Button
            label={t(termsOfUseAndDataPrivacyPolicyKeys.actions.proceed)}
            disabled={!sharedDialogState.isChecked || !sharedDialogState.isScrolledToBottom}
            onClick={onAcceptTermsOfUseAndPrivacyPolicy}
          />
        </div>
      </div>
    </div>
  );
};
